import { cloneDeep, isEmpty, uniq } from 'lodash-es';
import { BLOTTER_ID_OPEN_ORDERS } from '../../containers/Blotters/Orders/tokens';
import { TRADES_BLOTTER_PREFIX } from '../../containers/Blotters/Trades/tokens';
import { BLOTTER_ID_V1_OLD_RECENT_ORDERS, BLOTTER_ID_V1_OLD_RECENT_TRADES } from '../../tokens/blotters';
import { showcases } from '../../tokens/showcase';
import { tours } from '../../tokens/tours';
import { appVersion, getLatestReleaseVersion, isNonReleaseVersion } from '../../utils/version';
import { type GeneralMarketTab, type MarketTab, MarketTabType } from '../MarketTabs.types';
import { addTypeToLayoutPanelParams } from './addTypeToLayoutPanelParams';
import { enableSmartLabelledAnalyticsTabs } from './AppConfigMigrations/AnalyticsTabsMigration';
import {
  renameBalancesBlotterTabColumns,
  renamePersistedBalancesBlotterColumns,
} from './AppConfigMigrations/BalancesBlotterColumnMigrations';
import { migrateBlotters2ToBlotters3 } from './AppConfigMigrations/Blotters3Migration';
import { replaceDealerMonitoringFilters } from './AppConfigMigrations/DealerMonitoringMigration';
import type { AppConfigStateWithLegacyLayoutConfigs } from './AppConfigMigrations/legacyLayoutConfigs.types';
import { migrateBlotterStateToPanelParams } from './AppConfigMigrations/MigrateBlotterStateToPanelParams';
import { migrateBlotterTabsToPanelParams } from './AppConfigMigrations/migrateBlotterTabsToPanelParams/migrateBlotterTabsToPanelParams';
import { migrateCareOrdersBlotterStateToPanelParams } from './AppConfigMigrations/MigrateCareOrdersBlotterStateToPanelParams';
import { migrateCareOrdersPanelToTabs } from './AppConfigMigrations/MigrateCareOrdersPanelToTabs';
import { migrateRequiredPanels } from './AppConfigMigrations/migrateRequiredPanels';
import { migrateSpacesNavFromLayoutConfigs } from './AppConfigMigrations/migrateSpacesNavFromLayoutConfigs';
import { opsOverviewStateMigration } from './AppConfigMigrations/OpsOverviewStateMigration';
import {
  pruneOrderDetailsBlotterState,
  prunePersistedBlotterTableState,
} from './AppConfigMigrations/PrunePersistedBlotterTableState';
import { prunePersistedPortfolioManagementBlotterTableState } from './AppConfigMigrations/PrunePersistedPortfolioManagementBlotterTableState';
import { migrateRecentSymbolsToTimestampArrays } from './AppConfigMigrations/RecentSymbolsMigration';
import { ensureReconMismatchesBlotterTabsHaveStartDate } from './AppConfigMigrations/ReconMismatchesBlotterMigrations';
import { removeEnableNewMarketSelectorMigration } from './AppConfigMigrations/RemoveEnableNewMarketSelectorMigration';
import { removeLegacyReadOnlyTabComponentsFromBlotterPanels } from './AppConfigMigrations/removeLegacyReadOnlyTabComponentsFromBlotterPanels';
import { removeUseNewPositionsBlotter2 } from './AppConfigMigrations/RemoveUseNewPositionsBlotter2';
import type { AppConfigState } from './AppConfigProvider.types';
import { convertMarketTabsToDockviewLayout } from './convertMarketTabsToDockviewLayout';
import { isUntouchedMarketTabsImport } from './isUntouchedMarketTabsImport';
import { replaceBlotterV1Config } from './replaceBlotterV1Config';
import { replaceLegacyPinNavigationSidebarConfig } from './replaceLegacyPinNavigationSidebarConfig';
import { replaceLegacyPriceChartConfig } from './replaceLegacyPriceChartConfig';

export function replaceLegacyConfig(c: Record<string, any>): AppConfigState {
  const replacedConfig = cloneDeep(c);
  const keys = Object.keys(replacedConfig);
  // The initial config is empty or only has the marketTabs key, set the first seen version to the current version
  if (isEmpty(replacedConfig) || (keys.length === 1 && keys[0] === 'marketTabs')) {
    const currentVersion = appVersion(import.meta.env.VITE_NPM_PACKAGE_VERSION, true);
    // When version is 0.0.0 (non-release build), use the latest tour/showcase version
    // so new users don't see tours/showcases for features that already exist
    if (isNonReleaseVersion(currentVersion)) {
      replacedConfig.firstSeenVersion = getLatestReleaseVersion(tours, showcases);
    } else {
      replacedConfig.firstSeenVersion = currentVersion ?? '2.54.0';
    }
  } else if (replacedConfig.firstSeenVersion == null) {
    // If the config has other keys, set the first seen version to version before this was released
    replacedConfig.firstSeenVersion = '2.54.0';
  }
  if (c.subAccountId != null) {
    delete replacedConfig.subAccountId;
    replacedConfig.defaultSubAccountId = c.subAccountId;
  }
  if (c.order?.enableGroups != null) {
    delete replacedConfig.order;
    replacedConfig.enableGroups = c.order.enableGroups;
  }
  if (c.columns) {
    delete replacedConfig.columns;
  }

  // Remove old Dealer Blotter Filter IDs
  delete replacedConfig.filteredIDCustomerUsers;
  delete replacedConfig.filteredIDCustomerBalances;
  delete replacedConfig.filteredIDCustomerTradingLimits;

  if (replacedConfig.enableSalesRFQ != null) {
    replacedConfig.enableSalesWorkflow = replacedConfig.enableSalesRFQ;
    delete replacedConfig.enableSalesRFQ;
  }

  if (typeof c.customEOD === 'number') {
    // Some old configs have the custom EOD saved as a number representing hours
    c.customEOD = {
      hours: `${c.customEOD}`,
      minutes: DEFAULT_DATE_PICKER_EOD.minutes,
    };
  }

  function isMarketTabs(marketTabs: any): marketTabs is GeneralMarketTab[] {
    return marketTabs && Array.isArray(marketTabs);
  }

  // Ensure that the MarketDataCard configurations have selectedMarketAccounts defined
  if (isMarketTabs(replacedConfig.marketTabs)) {
    replacedConfig.marketTabs.forEach(marketTab => {
      if (marketTab && Array.isArray(marketTab.columns)) {
        marketTab.columns.forEach(col => {
          col.forEach(card => {
            card.availableMarketAccounts = uniq(card.availableMarketAccounts ?? []);
            card.selectedMarketAccounts = uniq(card.selectedMarketAccounts ?? []);
            card.availableMarkets = uniq(card.availableMarkets ?? []);
            card.selectedMarkets = uniq(card.selectedMarkets ?? []);
          });
        });
      }
    });
    // Dict format: seed default from market tabs, add panel param types, optionally refresh untouched import.
    // Array format: still run addTypeToLayoutPanelParams on every layout spec.
    if (!Array.isArray(replacedConfig.layoutConfigs?.trading)) {
      if (
        replacedConfig.layoutConfigs == null ||
        isEmpty(replacedConfig.layoutConfigs) ||
        replacedConfig.layoutConfigs.trading == null ||
        isEmpty(replacedConfig.layoutConfigs.trading) ||
        replacedConfig.layoutConfigs.trading.default == null ||
        isEmpty(replacedConfig.layoutConfigs.trading.default)
      ) {
        replacedConfig.layoutConfigs = {
          trading: Object.assign(replacedConfig.layoutConfigs?.trading ?? {}, {
            default: convertMarketTabsToDockviewLayout(replacedConfig.marketTabs),
          }),
        };
      }

      replacedConfig.layoutConfigs.trading = {
        ...replacedConfig.layoutConfigs.trading,
        default: replacedConfig.layoutConfigs.trading.default
          ? addTypeToLayoutPanelParams(replacedConfig.layoutConfigs.trading.default)
          : undefined,
      };

      // If the trading layout has not been used, convert market tabs to dockview layout again to get the latest changes
      if (
        replacedConfig.layoutConfigs.trading?.default?.dockViewLayout &&
        isUntouchedMarketTabsImport(replacedConfig.layoutConfigs.trading.default.dockViewLayout)
      ) {
        console.info('Detected untouched import of trading layout, converting market tabs to dockview layout');
        replacedConfig.layoutConfigs.trading.default = convertMarketTabsToDockviewLayout(replacedConfig.marketTabs);
      }
    }
    if (replacedConfig.spacesConfigs?.trading != null) {
      replacedConfig.spacesConfigs.trading.layouts = (replacedConfig.spacesConfigs.trading.layouts ?? []).map(
        (spec: any) => (spec?.dockViewLayout != null ? addTypeToLayoutPanelParams(spec) : spec)
      );
    }

    replaceLegacyPriceChartConfig(replacedConfig as AppConfigState);
  }

  if (replacedConfig.layoutConfigs != null) {
    // Dict-format trading layout (before migrateSpacesNavFromLayoutConfigs runs at end of pipeline)
    const rawTrading = replacedConfig.layoutConfigs.trading as Record<string, any> | undefined;

    // Default trading layout watchlist panel should use default tab component and not readOnly
    const watchlistPanel = rawTrading?.default?.dockViewLayout?.panels?.watchlist;
    if (watchlistPanel?.tabComponent === 'readOnly') {
      watchlistPanel.tabComponent = undefined;
    }

    // Remove symbolViews layout config
    if (rawTrading) {
      delete rawTrading.symbolViews;
    }
    if (replacedConfig.selectedLayoutIds?.trading === 'symbolViews') {
      replacedConfig.selectedLayoutIds.trading = 'default';
    }
  }

  // Migrate v1 recent orders blotter to v2
  if (replacedConfig.blotters != null && BLOTTER_ID_V1_OLD_RECENT_ORDERS in replacedConfig.blotters) {
    replaceBlotterV1Config(replacedConfig, BLOTTER_ID_V1_OLD_RECENT_ORDERS, BLOTTER_ID_OPEN_ORDERS, '-SubmitTime', {
      submitTime: 'SubmitTime',
      side: 'Side',
      symbol: 'Symbol',
      orderQty: 'OrderQty',
      cumQty: 'CumQty',
      price: 'Price',
      filledPrice: 'filledPx',
      cumFee: 'CumFee',
      marketAccounts: 'orderMarketAccounts',
      strategy: 'Strategy',
      subAccount: 'SubAccount',
      user: 'User',
      filledPercent: 'filledPercent',
      ordStatus: 'OrdStatus',
      filledPriceNoFees: 'AvgPx',
      filledPriceWithFees: 'AvgPxAllIn',
      markets: 'Markets',
      filledNotional: 'filledNotional',
      openNotional: 'openNotional',
      totalNotional: 'totalNotional',
      orderMarkets: 'orderMarkets',
      group: 'Group',
      productType: 'productType',
      endTime: 'EndTime',
      startTime: 'StartTime',
      timestamp: 'Timestamp',
      baseQuantity: 'baseQty',
      ordType: 'OrdType',
      orderID: 'OrderID',
      clOrdID: 'ClOrdID',
      leavesQty: 'LeavesQty',
      remainQuantity: 'remainQty',
      timeInForce: 'TimeInForce',
    });
  }

  // Migrate v1 recent trades blotter to v2
  if (replacedConfig.blotters != null && BLOTTER_ID_V1_OLD_RECENT_TRADES in replacedConfig.blotters) {
    replaceBlotterV1Config(
      replacedConfig,
      BLOTTER_ID_V1_OLD_RECENT_TRADES,
      `${TRADES_BLOTTER_PREFIX}/recent-trades`,
      '-TransactTime',
      {
        transactTime: 'TransactTime',
        side: 'Side',
        symbol: 'Symbol',
        quantity: 'Quantity',
        price: 'Price',
        priceAllIn: 'PriceAllIn',
        amount: 'Amount',
        fee: 'Fee',
        user: 'User',
        tradeID: 'TradeID',
        marketAccount: 'MarketAccount',
        market: 'Market',
        subAccount: 'SubAccount',
        group: 'Group',
        orderID: 'OrderID',
        marketTradeID: 'MarketTradeID',
        timestamp: 'Timestamp',
        dealtCurrency: 'DealtCurrency',
        tradeStatus: 'TradeStatus',
        aggressorSide: 'AggressorSide',
        comments: 'Comments',
      }
    );
  }

  // Remove any temporary market tab and ensure mandatory property exist
  if (replacedConfig.marketTabs != null) {
    replacedConfig.marketTabs = replacedConfig.marketTabs
      .filter((tab: MarketTab) => !tab.isTemporary)
      .map((tab: MarketTab) => {
        if (!tab.type) {
          // Some configs may not have type saved before, we need to fix that as it's mandatory field now
          (tab as GeneralMarketTab).type = MarketTabType.Market;
        }
        return tab;
      });
  }

  ensureReconMismatchesBlotterTabsHaveStartDate(replacedConfig);
  renamePersistedBalancesBlotterColumns(replacedConfig);
  renameBalancesBlotterTabColumns(replacedConfig);
  removeUseNewPositionsBlotter2(replacedConfig);
  removeEnableNewMarketSelectorMigration(replacedConfig);
  replaceDealerMonitoringFilters(replacedConfig);
  migrateRecentSymbolsToTimestampArrays(replacedConfig);
  migrateBlotters2ToBlotters3(replacedConfig);
  prunePersistedBlotterTableState(replacedConfig);
  pruneOrderDetailsBlotterState(replacedConfig);
  removeLegacyReadOnlyTabComponentsFromBlotterPanels(replacedConfig);
  migrateBlotterTabsToPanelParams(replacedConfig);
  enableSmartLabelledAnalyticsTabs(replacedConfig);
  prunePersistedPortfolioManagementBlotterTableState(replacedConfig);
  opsOverviewStateMigration(replacedConfig);
  replaceLegacyPinNavigationSidebarConfig(replacedConfig as AppConfigState);
  migrateRequiredPanels(replacedConfig);
  migrateSpacesNavFromLayoutConfigs(replacedConfig as AppConfigStateWithLegacyLayoutConfigs);
  migrateBlotterStateToPanelParams(replacedConfig);
  migrateCareOrdersBlotterStateToPanelParams(replacedConfig);
  migrateCareOrdersPanelToTabs(replacedConfig);

  return replacedConfig as AppConfigState;
}
export const DEFAULT_DATE_PICKER_EOD = { hours: '17', minutes: '00' };
